import { useState, useEffect } from 'react';
import useSWR, { mutate } from 'swr';

import { SESSION_TIMEOUT, FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

import * as API from '@/lib/tabularCompareApi';
import { IResponseError } from '@/lib/apiBase';
import { IRepository } from '@/types';

const revalidate = (compareNumber: string) => {
  const key = ['compare', compareNumber];
  // console.log('revalidate > key', key);
  mutate(key, undefined, { revalidate: true });
};

const useTabularCompare = ({
  repository,
  compareNumber,
  initialData,
}: {
  repository: IRepository;
  compareNumber: number;
  initialData?: API.ICompareResponse;
}) => {
  const [compare, setCompare] = useState<API.TComparison | undefined>(initialData?.compare);
  const [status, setStatus] = useState<'success' | 'error' | 'pending' | 'loading'>(
    !!initialData?.compare ? 'success' : 'pending',
  );
  const [error, setError] = useState<IResponseError | undefined>();

  const fetchKey = compareNumber ? ['compare', compareNumber] : null;

  const {
    data,
    error: requestError,
    isLoading,
    isValidating,
  } = useSWR(fetchKey, () => API.fetchCompare(repository.namespace, repository.name, compareNumber.toString()), {
    fallbackData: initialData,
    keepPreviousData: true,
    revalidateOnFocus: false,
    revalidateOnMount: !initialData?.compare,
    revalidateOnReconnect: false,
    focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
  });

  // console.log('useTabularCompare > data', data);
  // console.log('useTabularCompare > error', error);
  // console.log('useTabularCompare > isLoading', isLoading);
  // console.log('useTabularCompare > isValidating', isValidating);

  useEffect(() => {
    if (data) {
      // console.log('useTabularCompare > data', data);
      setCompare(data?.compare);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  const updateSuccess = (response: API.ICompareResponse): void => {
    setError(undefined);
    const { compare } = response;
    // console.log('updateSuccess > organization', organization);
    if (compare) {
      setCompare(compare);
      revalidate(compare.compare_number.toString());
    }
  };

  const updateFailed = (response: API.ICompareResponse): void => {
    setError(response.error as IResponseError);
    setCompare(undefined);
  };

  const updateCompare = async ({ title, description }: { title?: string; description?: string }): Promise<string> => {
    setStatus('loading');
    // TODO: this is a total hack, figure out how this should be managed as a convention
    // if (!organization) {
    //   return 'error';
    // }
    if (!compare) {
      return 'error';
    }
    const def = compare.definition;
    def.title = title || def.title;
    def.description = description || def.description;

    const response = await API.updateCompare({
      compareId: compare?.compare_number.toString() as string,
      namespace: repository.namespace,
      repositoryName: repository.name,
      payload: def,
    } as API.IUpdateCompareParams);

    // console.log('updateOrganization > response', response);
    setStatus(response.status);

    if (response.status === 'success') {
      updateSuccess(response);
    } else {
      updateFailed(response);
    }
    return response?.status;
  };

  const loading: boolean = isValidating && !compare;

  return { loading, compare, updateCompare, status, error };
};

// export const Organization = createContainer(useTabularCompare);

export default useTabularCompare;
